/**
 * Haptic feedback utilities for touch interactions
 * Apple-inspired tactile feedback patterns for premium UX
 * Provides tactile feedback on supported devices
 * 
 * Now uses platform layer for native app support
 */

import { hapticImpact } from './platform';

type HapticIntensity = 'light' | 'medium' | 'strong';

/**
 * Triggers a haptic feedback pulse with configurable intensity
 * Safe to call on devices that don't support vibration
 * Uses platform layer for native app support
 */
export function triggerHaptic(intensity: HapticIntensity = 'light'): void {
  // Map 'strong' to 'heavy' for platform layer
  const style = intensity === 'strong' ? 'heavy' : intensity;
  hapticImpact(style).catch(() => {
    // Silently fail - haptics are non-critical
  });
}

/**
 * Triggers a success haptic pattern - celebratory double tap
 * Use for: completing tasks, achievements, successful submissions
 */
export function triggerHapticSuccess(): void {
  // Use medium impact for success
  hapticImpact('medium').catch(() => {
    // Fallback to web vibrate pattern if native fails
    if ('vibrate' in navigator) {
      navigator.vibrate([5, 50, 5, 50, 10]);
    }
  });
}

/**
 * Triggers a streak/achievement celebration pattern
 * Use for: streak milestones, level ups, special achievements
 */
export function triggerHapticCelebration(): void {
  // Use heavy impact for celebration
  hapticImpact('heavy').catch(() => {
    // Fallback to web vibrate pattern if native fails
    if ('vibrate' in navigator) {
      navigator.vibrate([5, 30, 5, 30, 5, 30, 10, 50, 15]);
    }
  });
}

/**
 * Triggers an error haptic pattern - quick double pulse
 * Use for: errors, validation failures, destructive actions
 */
export function triggerHapticError(): void {
  // Use medium impact for errors
  hapticImpact('medium').catch(() => {
    // Fallback to web vibrate pattern if native fails
    if ('vibrate' in navigator) {
      navigator.vibrate([10, 30, 15]);
    }
  });
}
